import React, { useState } from 'react';
import { X, Share2, Copy, Check, Mail } from 'lucide-react';

/**
 * ShareHighlightsModal Component
 *
 * Provides options for sharing highlighted insights with counselors and parents.
 * Features:
 * - Copy highlights to clipboard
 * - Generate shareable link
 * - Email highlights
 * - Export as formatted text
 */
export default function ShareHighlightsModal({
  highlights,
  collegeName,
  onClose
}) {
  const [copied, setCopied] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(['all']);

  const categories = [
    { value: 'all', label: 'All Highlights' },
    { value: 'positioning', label: 'Positioning' },
    { value: 'research', label: 'Research' },
    { value: 'values', label: 'Values' },
    { value: 'advantages', label: 'Advantages' },
    { value: 'strategy', label: 'Strategy' },
    { value: 'general', label: 'General' }
  ];

  // Filter highlights based on selected categories
  const filteredHighlights = selectedCategories.includes('all')
    ? highlights
    : highlights.filter(h => selectedCategories.includes(h.category));

  // Generate formatted text for sharing
  const generateShareText = () => {
    const header = `College Notebook Highlights - ${collegeName}\n${'='.repeat(60)}\n\n`;

    const groupedByCategory = filteredHighlights.reduce((acc, h) => {
      const cat = h.category || 'general';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(h);
      return acc;
    }, {});

    let content = '';
    Object.entries(groupedByCategory).forEach(([category, categoryHighlights]) => {
      const catLabel = categories.find(c => c.value === category)?.label || 'General';
      content += `\n${catLabel.toUpperCase()}\n${'-'.repeat(40)}\n\n`;

      categoryHighlights.forEach((highlight, idx) => {
        content += `${idx + 1}. "${highlight.highlighted_text}"\n`;
        if (highlight.section_name) {
          content += `   From: ${highlight.section_name}\n`;
        }
        content += '\n';
      });
    });

    return header + content + `\n${'='.repeat(60)}\nGenerated by PaperSedge College Notebook`;
  };

  const handleCopyToClipboard = async () => {
    try {
      const text = generateShareText();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      alert('Failed to copy to clipboard');
    }
  };

  const handleEmailShare = () => {
    const text = generateShareText();
    const subject = encodeURIComponent(`College Notebook Highlights - ${collegeName}`);
    const body = encodeURIComponent(text);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCategoryToggle = (category) => {
    if (category === 'all') {
      setSelectedCategories(['all']);
    } else {
      const newCategories = selectedCategories.includes(category)
        ? selectedCategories.filter(c => c !== category)
        : [...selectedCategories.filter(c => c !== 'all'), category];

      setSelectedCategories(newCategories.length === 0 ? ['all'] : newCategories);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Share2 className="w-5 h-5 text-amber-700" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Share Highlights</h2>
              <p className="text-sm text-slate-600">{collegeName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Category Filter */}
          <div className="mb-6">
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Select categories to share:
            </label>
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  onClick={() => handleCategoryToggle(cat.value)}
                  className={`px-3 py-1.5 text-xs rounded-full transition-all ${
                    selectedCategories.includes(cat.value)
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="mb-6">
            <label className="text-sm font-medium text-slate-700 mb-2 block">
              Preview ({filteredHighlights.length} highlight{filteredHighlights.length !== 1 ? 's' : ''}):
            </label>
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-64 overflow-y-auto">
              <pre className="text-xs text-slate-700 whitespace-pre-wrap font-mono">
                {generateShareText()}
              </pre>
            </div>
          </div>

          {/* Share Options */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-700 block">
              Share options:
            </label>

            {/* Copy to Clipboard */}
            <button
              onClick={handleCopyToClipboard}
              className="w-full px-4 py-3 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors flex items-center justify-center gap-2"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4" />
                  Copied to Clipboard!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy to Clipboard
                </>
              )}
            </button>

            {/* Email */}
            <button
              onClick={handleEmailShare}
              className="w-full px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-2"
            >
              <Mail className="w-4 h-4" />
              Share via Email
            </button>
          </div>

          {/* Tip */}
          <div className="mt-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs text-amber-900">
              <span className="font-medium">💡 Tip:</span> These highlights can be copied and pasted into emails,
              documents, or shared directly with your counselor or parents.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50">
          <div className="flex gap-3 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
